// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import { getFormConfig, getSectionColors } from '../form-definitions';
import { usePdfGenerator } from '../hooks/usePdfGenerator';
import { Button } from '../components/ui/button';
import AuditFieldRenderer from '../components/audit/AuditFieldRenderer';
import ShareFormDialog from '../components/audit/ShareFormDialog';
import NextMonthS26Dialog from '../components/audit/NextMonthS26Dialog';
import S26CheckDialog from '../components/audit/S26CheckDialog';
import ImportS26Dialog from '../components/audit/ImportS26Dialog';
import { S26LedgerTable } from '../components/S26LedgerTable';
import { computeS26Sync, parseBR, formatBR, sortS26Rows } from '../components/s26Sync';
import {
  extractFieldsFromTemplate,
  buildInitialValues,
  buildPreviewBytes,
} from '../lib/pdfFieldLoader';
import {
  Save,
  Loader2,
  AlertCircle,
  ClipboardList,
  Share2,
  Download,
  X,
  Eye,
  FileText,
  CalendarPlus,
  ArrowRightLeft,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function hasValue(v) {
  if (typeof v === 'boolean') return true;
  return !!v && String(v).trim() !== '';
}

export default function AuditFormEditorPage() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { flattenPdfWithValues, fillPdfFields, downloadPdf } = usePdfGenerator();

  const [formData, setFormData] = useState(null);
  const [formType, setFormType] = useState('s25b');
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [pdfBytes, setPdfBytes] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [error, setError] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showMobilePdf, setShowMobilePdf] = useState(false);
  const [showNextMonth, setShowNextMonth] = useState(false);
  const [showImportS26, setShowImportS26] = useState(false);
  const [showS26Check, setShowS26Check] = useState(false);

  useEffect(() => {
    if (formId) loadForm();
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const loadForm = async () => {
    setLoading(true);
    try {
      const fieldValuesRes = await axios.get(`${API}/audit-forms/${formId}/field-values`);
      const formMeta = fieldValuesRes.data;
      const savedValues = formMeta.field_values || {};
      const detectedType = formMeta.form_type || 's25b';
      setFormType(detectedType);
      setFormData({ title: formMeta.title, status: formMeta.status, form_type: detectedType });

      // Always fetch the clean TEMPLATE for field extraction.
      const templateRes = await axios.get(
        `${API}/audit-forms/template/${detectedType}/download`,
        { responseType: 'arraybuffer' },
      );
      const templateBytes = new Uint8Array(templateRes.data);
      setPdfBytes(templateBytes);

      const extracted = await extractFieldsFromTemplate(templateBytes, detectedType);
      setFields(extracted);

      const initVals = buildInitialValues(extracted, savedValues);
      setValues(initVals);

      const hasSaved = Object.keys(savedValues).length > 0;
      const previewBytes = hasSaved
        ? await buildPreviewBytes(templateBytes, extracted, initVals)
        : templateBytes;
      setPreviewUrl(URL.createObjectURL(new Blob([previewBytes], { type: 'application/pdf' })));

      const firstSec = extracted.find(f => f.section)?.section;
      if (firstSec) setActiveSection(firstSec);
    } catch (err) {
      console.error(err);
      setError('Erro ao carregar o formulário. Verifique se está logado.');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = useCallback((name, value) => {
    setValues(prev => ({ ...prev, [name]: value }));
  }, []);

  // S-26: totais do registro e Quadro Resumo recalculados a cada alteração (pula o carregamento inicial).
  const s26SyncReady = useRef(false);
  useEffect(() => {
    if (formType !== 's26' || loading) return;
    if (!s26SyncReady.current) { s26SyncReady.current = true; return; }
    const computed = computeS26Sync(values);
    const changed = Object.entries(computed).filter(([k, v]) => (values[k] || '') !== v);
    if (changed.length) {
      setValues(prev => ({ ...prev, ...Object.fromEntries(changed) }));
    }
  }, [values, formType, loading]);

  const generateFilledPdf = async (shouldFlatten = false, vals = values) => {
    if (!pdfBytes) return null;
    return shouldFlatten
      ? await flattenPdfWithValues(formType, vals)
      : await fillPdfFields(pdfBytes, fields, vals);
  };

  // S-26: garante lançamentos em ordem cronológica pelo Dia antes de gerar/salvar o PDF.
  const getSortedValues = () => {
    if (formType !== 's26') return values;
    const updates = sortS26Rows(values);
    if (!updates) return values;
    const sorted = { ...values, ...updates };
    setValues(sorted);
    return sorted;
  };

  const formConfig = getFormConfig(formType);
  const formLabel = formConfig.title;

  const handleSave = async () => {
    if (!pdfBytes) return;
    setSaving(true);
    try {
      const vals = getSortedValues();
      const filledBytes = await generateFilledPdf(false, vals);
      if (!filledBytes) return;

      const blob = new Blob([filledBytes], { type: 'application/pdf' });
      const formDataUpload = new FormData();
      formDataUpload.append('file', blob, `${formLabel}.pdf`);
      formDataUpload.append('field_values', JSON.stringify(vals));

      await axios.put(`${API}/audit-forms/${formId}/update-content`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Formulário salvo com sucesso!');

      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(new Blob([filledBytes], { type: 'application/pdf' })));
    } catch (err) {
      toast.error('Erro ao salvar');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const performDownload = async () => {
    try {
      const filledBytes = await generateFilledPdf(true, getSortedValues());
      if (!filledBytes) return;
      downloadPdf(filledBytes, `${formLabel}_${formData?.title || 'Relatorio'}.pdf`);
      toast.success('PDF baixado!');
    } catch { toast.error('Erro ao gerar PDF'); }
  };

  // S-26: mostra o resumo de conferência antes de gerar o PDF.
  const handleDownload = () => {
    if (formType === 's26') setShowS26Check(true);
    else performDownload();
  };

  const handleShareWhatsApp = async () => {
    const title = formData?.title || `Relatório ${formLabel}`;
    try {
      const filledBytes = await flattenPdfWithValues(formType, values);
      if (!filledBytes) { toast.error('Erro ao gerar PDF'); return; }

      const blob = new Blob([filledBytes], { type: 'application/pdf' });
      const fileName = `${formLabel}_${title}.pdf`;
      const file = new File([blob], fileName, { type: 'application/pdf' });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `Relatório ${formLabel}: ${title}`,
          text: `Relatório de Auditoria ${formLabel}: ${title}\n\nCompartilhado via JW Audit`,
          files: [file],
        });
        toast.success('Relatório compartilhado!');
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
        const text = encodeURIComponent(
          `Relatório de Auditoria ${formLabel}: ${title}\n\n(PDF baixado em anexo)\n\nCompartilhado via JW Audit`,
        );
        window.open(`https://wa.me/?text=${text}`, '_blank');
        toast.success('PDF baixado! Anexe-o na conversa do WhatsApp.');
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        toast.error('Erro ao compartilhar');
        console.error(e);
      }
    }
  };

  const handleShareEmail = async () => {
    if (!shareEmail.trim()) { toast.error('Digite o email do destinatário'); return; }
    setSendingEmail(true);
    try {
      const filledBytes = await generateFilledPdf(false);
      if (filledBytes) {
        const blob = new Blob([filledBytes], { type: 'application/pdf' });
        const formDataUpload = new FormData();
        formDataUpload.append('file', blob, `${formLabel}.pdf`);
        formDataUpload.append('field_values', JSON.stringify(values));
        await axios.put(`${API}/audit-forms/${formId}/update-content`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      const flattenedBytes = await generateFilledPdf(true);
      if (flattenedBytes) {
        const flatBlob = new Blob([flattenedBytes], { type: 'application/pdf' });
        const emailFormData = new FormData();
        emailFormData.append('file', flatBlob, `${formLabel}_${formData?.title || 'Relatorio'}.pdf`);
        emailFormData.append('email', shareEmail.trim());
        emailFormData.append('message', shareMessage.trim());
        await axios.post(`${API}/audit-forms/${formId}/share-email-with-file`, emailFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      toast.success(`Relatório enviado para ${shareEmail}`);
      setShowShare(false);
      setShareEmail('');
      setShareMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleMobileViewPdf = async () => {
    try {
      const filledBytes = await generateFilledPdf(true);
      if (filledBytes) {
        const blob = new Blob([filledBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      }
    } catch {
      if (previewUrl) window.open(previewUrl, '_blank');
    }
  };

  // Group fields by section
  const sectionColors = getSectionColors(formType);
  const sections = [];
  const sectionMap = new Map();
  const isS26 = formType === 's26';
  const isS44 = formType === 's44';
  const isLedgerSection = (sec) => isS26 && (sec.startsWith('Registro —') || sec === 'Totais do Registro');
  let ledgerInserted = false;
  fields.forEach(f => {
    const sec = f.section || 'Outros';
    if (isLedgerSection(sec)) {
      if (!ledgerInserted) { sections.push('Lançamentos'); ledgerInserted = true; }
      if (!sectionMap.has('Lançamentos')) sectionMap.set('Lançamentos', []);
      sectionMap.get('Lançamentos').push(f);
      return;
    }
    if (!sectionMap.has(sec)) { sectionMap.set(sec, []); sections.push(sec); }
    sectionMap.get(sec).push(f);
  });

  const getSectionProgress = (sec) => {
    const sf = sectionMap.get(sec) || [];
    return { filled: sf.filter(f => hasValue(values[f.name])).length, total: sf.length };
  };

  const totalFilled = fields.filter(f => hasValue(values[f.name])).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Carregando formulário...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <p className="text-foreground font-medium">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background" data-testid="audit-form-editor-standalone">
      {/* Header */}
      <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 border-b border-border bg-card shadow-sm flex-shrink-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <ClipboardList className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-sm sm:text-lg font-bold text-foreground truncate">
              {(formData?.title || 'Relatório').startsWith(formLabel) ? formData?.title : `${formLabel} ${formData?.title || 'Relatório'}`}
            </h1>
            <p className="text-xs text-muted-foreground">
              {totalFilled}/{fields.length} campos
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {isS26 && (
            <Button variant="outline" size="sm" onClick={() => setShowNextMonth(true)} data-testid="next-month-btn" title="Gerar folha do mês seguinte">
              <CalendarPlus className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Próximo mês</span>
            </Button>
          )}
          {isS44 && (
            <Button variant="outline" size="sm" onClick={() => setShowImportS26(true)} data-testid="import-s26-btn" title="Importar lançamentos do S-26">
              <ArrowRightLeft className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Importar S-26</span>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleDownload} data-testid="download-form-btn" title="Baixar PDF">
            <Download className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Baixar</span>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowShare(true)} data-testid="share-form-btn" title="Compartilhar">
            <Share2 className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Compartilhar</span>
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-form-btn">
            {saving ? <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" /> : <Save className="w-4 h-4 sm:mr-2" />}
            <span className="hidden sm:inline">{saving ? 'Salvando...' : 'Salvar'}</span>
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        <div className="hidden md:block md:w-[45%] border-r border-border">
          {previewUrl && <iframe src={`${previewUrl}#toolbar=0`} className="w-full h-full" title="PDF" />}
        </div>

        {showMobilePdf && previewUrl && (
          <div className="fixed inset-0 z-50 bg-background flex flex-col md:hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-card">
              <span className="text-sm font-semibold text-foreground">Visualização do PDF</span>
              <Button variant="ghost" size="sm" onClick={() => setShowMobilePdf(false)} data-testid="close-mobile-pdf">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="flex-1 overflow-auto">
              <iframe src={`${previewUrl}#toolbar=0&navpanes=0`} className="w-full h-full min-h-[80vh]" title="PDF Preview" />
            </div>
          </div>
        )}

        <div className="flex-1 md:w-[55%] flex flex-col overflow-hidden">
          <div className="flex md:hidden items-center gap-2 px-3 py-2 bg-muted/50 border-b border-border">
            <FileText className="w-4 h-4 text-primary flex-shrink-0" />
            <span className="text-xs text-muted-foreground flex-1">PDF do relatório</span>
            <Button variant="outline" size="sm" className="h-8 text-xs"
              onClick={handleMobileViewPdf}
              data-testid="mobile-view-pdf-btn">
              <Eye className="w-3.5 h-3.5 mr-1" /> Visualizar
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs"
              onClick={handleDownload} data-testid="mobile-download-pdf-btn">
              <Download className="w-3.5 h-3.5" />
            </Button>
          </div>

          {/* Section tabs */}
          <div className="flex gap-1 overflow-x-auto p-3 pb-2 flex-shrink-0 border-b border-border">
            {sections.map(sec => {
              const { filled, total } = getSectionProgress(sec);
              const color = sectionColors[sec] || 'bg-slate-500';
              return (
                <button
                  key={sec}
                  onClick={() => setActiveSection(sec)}
                  data-testid={`section-tab-${sec}`}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    activeSection === sec
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'bg-muted text-muted-foreground hover:bg-accent'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${color}`} />
                    {sec}
                    <span className="opacity-60">({filled}/{total})</span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {activeSection === 'Lançamentos' && isS26 ? (
              <S26LedgerTable values={values} onChange={handleFieldChange} />
            ) : (
              (sectionMap.get(activeSection) || []).map(field => (
                <AuditFieldRenderer
                  key={field.name}
                  field={field}
                  value={values[field.name]}
                  onChange={handleFieldChange}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <ShareFormDialog
        open={showShare}
        onOpenChange={setShowShare}
        formLabel={formLabel}
        onDownload={handleDownload}
        onShareWhatsApp={handleShareWhatsApp}
        email={shareEmail}
        setEmail={setShareEmail}
        message={shareMessage}
        setMessage={setShareMessage}
        sendingEmail={sendingEmail}
        onSendEmail={handleShareEmail}
      />

      <NextMonthS26Dialog
        open={showNextMonth}
        onOpenChange={setShowNextMonth}
        form={{ id: formId, title: formData?.title }}
        onCreated={(newForm) => navigate(`/audit-form/edit/${newForm.id}`)}
      />

      <S26CheckDialog
        open={showS26Check}
        onOpenChange={setShowS26Check}
        values={values}
        onConfirm={performDownload}
      />

      <ImportS26Dialog
        open={showImportS26}
        onOpenChange={setShowImportS26}
        onImport={(result) => setValues(prev => {
          const merged = { ...prev, ...result.values };
          if ((merged['901_3_S44BOM'] || '').trim()) {
            const saldoInicial = parseBR(merged['901_3_S44BOM']);
            merged['901_49_S44EOM'] = formatBR(saldoInicial + result.totals.entradas - result.totals.despesas);
          }
          return merged;
        })}
      />
    </div>
  );
}
